import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Tv, Plug, CheckCircle2, Plus, Trash2, Eye, Send, BarChart3, Film, Gauge, X, ShieldAlert, MapPin } from "lucide-react";
import { sr } from "../../lib/sr";

const money = (v) => `$${Number(v || 0).toLocaleString()}`;
const STATUS = {
  draft: ["Draft", "sr-badge-gray"], submitted: ["Submitted to ad platform", "sr-badge-green"],
  error: ["Needs attention", "sr-badge-amber"],
};
const PACE = {
  on_track: ["On track", "sr-badge-green"], under: ["Under-pacing", "sr-badge-amber"],
  over: ["Over-pacing", "sr-badge-amber"], scheduled: ["Scheduled", "sr-badge-gray"],
  awaiting: ["Awaiting delivery", "sr-badge-gray"], ended: ["Flight ended", "sr-badge-gray"],
};
const estViews = (b) => Math.round((Number(b) || 0) / 10 * 1000);
const LS_ADV = "sr_dooh_advertiser";

export default function DoohAds() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [orders, setOrders] = useState(null);
  const [advertisers, setAdvertisers] = useState([]);
  const [delivery, setDelivery] = useState({});
  const [busy, setBusy] = useState(false);
  const [actId, setActId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [movies, setMovies] = useState(null);
  const [form, setForm] = useState({
    name: params.get("name") || "", budget: "", start: "", end: "",
    markets: "", creative_url: params.get("poster") || "",
    advertiser_id: localStorage.getItem(LS_ADV) || "", source_item_id: params.get("item") || "",
    geofence: false, geofence_radius_mi: 3,
  });
  const [geoPreview, setGeoPreview] = useState(null);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const load = () => Promise.all([sr.doohStatus(), sr.doohOrders(), sr.doohAdvertisers()])
    .then(([s, o, a]) => {
      setStatus(s); setOrders(o.orders || []); setAdvertisers(a.advertisers || []);
      setForm((p) => ({ ...p, advertiser_id: p.advertiser_id || s.default_advertiser_id || "" }));
    })
    .catch(() => { setStatus({ connected: false }); setOrders([]); });
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!form.geofence) { setGeoPreview(null); return; }
    const markets = form.markets.split(",").map((m) => m.trim()).filter(Boolean);
    if (markets.length === 0) { setGeoPreview({ count: 0, configured: true }); return; }
    let cancelled = false;
    setGeoPreview({ loading: true });
    const t = setTimeout(() => {
      sr.doohGeofencePreview(markets.join(","), form.geofence_radius_mi)
        .then((r) => { if (!cancelled) setGeoPreview(r); })
        .catch(() => { if (!cancelled) setGeoPreview({ count: 0, configured: true }); });
    }, 600);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.geofence, form.markets, form.geofence_radius_mi]);

  const openPicker = async () => {
    setPickerOpen(true);
    if (movies === null) {
      try { const r = await sr.doohCreatives(); setMovies(r.movies || []); }
      catch { setMovies([]); }
    }
  };
  const chooseCreative = (url, itemId) => {
    setForm((p) => ({ ...p, creative_url: url, source_item_id: itemId || p.source_item_id }));
    setPickerOpen(false);
    toast.success("Creative selected");
  };

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name the DOOH order");
    if (!(parseFloat(form.budget) > 0)) return toast.error("Enter a budget");
    if (!form.start || !form.end) return toast.error("Pick flight start and end dates");
    setBusy(true);
    try {
      if (form.advertiser_id) localStorage.setItem(LS_ADV, form.advertiser_id);
      const body = {
        name: form.name.trim(), budget: parseFloat(form.budget), start: form.start, end: form.end,
        markets: form.markets.split(",").map((m) => m.trim()).filter(Boolean),
        creative_url: form.creative_url || undefined, advertiser_id: form.advertiser_id || undefined,
        source_item_id: form.source_item_id || undefined,
        geofence: !!form.geofence,
        geofence_radius_mi: form.geofence ? (parseFloat(form.geofence_radius_mi) || 3) : undefined,
      };
      await sr.createDoohOrder(body);
      toast.success("DOOH order saved");
      setForm((p) => ({ ...p, name: "", budget: "", markets: "" }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create the order"); }
    finally { setBusy(false); }
  };

  const act = async (id, fn, okMsg) => {
    setActId(id);
    try { await fn(); if (okMsg) toast.success(okMsg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setActId(null); }
  };
  const getDelivery = async (id) => {
    setActId(id);
    try { const r = await sr.doohDelivery(id); setDelivery((p) => ({ ...p, [id]: r })); load(); }
    catch (e) { toast.error("Could not fetch delivery"); }
    finally { setActId(null); }
  };

  if (!orders) return <div className="sr-empty" style={{ padding: 60, textAlign: "center" }}><Loader2 className="sr-spin" /></div>;

  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: "8px 4px 40px" }} data-testid="dooh-page">
      <h1 style={{ color: "var(--navy)", fontSize: 26, margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
        <Tv size={22} /> DOOH Ads
      </h1>
      <p className="sr-muted" style={{ marginTop: 6 }}>
        Place Digital Out-of-Home orders (billboards, transit &amp; venue screens) for any campaign through the the ad platform DSP, pick a creative, and track impressions and pacing.
      </p>

      {/* Connection status */}
      {status?.connected ? (
        <div className="sr-card" style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }} data-testid="dooh-connected">
          <CheckCircle2 size={16} style={{ color: "#0E7A55" }} />
          <span style={{ color: "var(--navy)", fontWeight: 600 }}>Ad platform connected</span>
          <span className="sr-muted" style={{ fontSize: 12 }}>· {advertisers.length} advertiser{advertisers.length === 1 ? "" : "s"} available</span>
        </div>
      ) : (
        <div className="sr-card" style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8, background: "#FFF7ED", border: "1px solid #FED7AA" }} data-testid="dooh-disconnected">
          <Plug size={16} style={{ color: "#B45309" }} />
          <span style={{ color: "#92400E", fontSize: 13.5 }}>Your ad platform isn't connected yet — add your ad platform API token to submit orders. You can still draft orders and see estimated views below.</span>
        </div>
      )}
      {status?.connected && status?.test_mode && (
        <div className="sr-card" style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, background: "#F8FAFC", border: "1px solid var(--border)" }} data-testid="dooh-testmode">
          <ShieldAlert size={15} style={{ color: "#64748B" }} />
          <span className="sr-muted" style={{ fontSize: 13 }}>Test Mode is ON — orders save as drafts. An admin switches to Production Mode to take an order live on the ad platform.</span>
        </div>
      )}

      {/* New order */}
      <div className="sr-card" style={{ marginTop: 14 }} data-testid="dooh-new-order">
        <strong style={{ color: "var(--navy)" }}>New DOOH order</strong>
        <div className="sr-field" style={{ marginTop: 10 }}><label className="sr-label">Order name</label>
          <input className="sr-input" value={form.name} onChange={set("name")} placeholder="e.g. Midnight in Mumbai — Opening Week DOOH" data-testid="dooh-name" /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <div className="sr-field"><label className="sr-label">Budget ($)</label>
            <input className="sr-input" type="number" min="0" value={form.budget} onChange={set("budget")} placeholder="5000" data-testid="dooh-budget" /></div>
          <div className="sr-field"><label className="sr-label">Flight start</label>
            <input className="sr-input" type="date" value={form.start} onChange={set("start")} data-testid="dooh-start" /></div>
          <div className="sr-field"><label className="sr-label">Flight end</label>
            <input className="sr-input" type="date" value={form.end} onChange={set("end")} data-testid="dooh-end" /></div>
        </div>
        {status?.connected && (
          <div className="sr-field"><label className="sr-label">Ad platform advertiser</label>
            <select className="sr-input" value={form.advertiser_id} onChange={set("advertiser_id")} data-testid="dooh-advertiser">
              <option value="">Select an advertiser…</option>
              {advertisers.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select></div>
        )}
        <div className="sr-field"><label className="sr-label">Markets <span className="sr-muted" style={{ fontWeight: 400 }}>· comma-separated</span></label>
          <input className="sr-input" value={form.markets} onChange={set("markets")} placeholder="New York, Los Angeles, Chicago" data-testid="dooh-markets" /></div>
        <div className="sr-field" style={{ background: "#F8FAFC", border: "1px solid var(--border)", borderRadius: 10, padding: "10px 12px" }}>
          <label className="sr-label" style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 0 }}>
            <input type="checkbox" checked={form.geofence} onChange={(e) => setForm((p) => ({ ...p, geofence: e.target.checked }))} data-testid="dooh-geofence-toggle" />
            <MapPin size={14} /> Target screens near theaters in these markets
          </label>
          {form.geofence && (
            <div style={{ marginTop: 10, paddingLeft: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="sr-muted" style={{ fontSize: 12.5 }}>Radius</span>
                <input className="sr-input" type="number" min="0.5" max="50" step="0.5" style={{ width: 90 }}
                  value={form.geofence_radius_mi} onChange={(e) => setForm((p) => ({ ...p, geofence_radius_mi: e.target.value }))} data-testid="dooh-geofence-radius" />
                <span className="sr-muted" style={{ fontSize: 12.5 }}>miles around each theater</span>
              </div>
              <div style={{ marginTop: 8, fontSize: 12.5 }} data-testid="dooh-geofence-preview">
                {geoPreview?.loading ? <span className="sr-muted"><Loader2 size={11} className="sr-spin" style={{ verticalAlign: "-2px" }} /> Finding theaters…</span>
                  : geoPreview && geoPreview.configured === false ? <span className="sr-muted">Location lookup isn't set up here — the order still runs across your markets.</span>
                  : geoPreview && geoPreview.count > 0 ? <span style={{ color: "#0E7A55" }}><MapPin size={11} style={{ verticalAlign: "-2px" }} /> Targeting {geoPreview.count} theater{geoPreview.count === 1 ? "" : "s"} within {geoPreview.radius_mi} mi{geoPreview.points?.length ? ` — ${geoPreview.points.slice(0, 3).map((p) => p.name).join(", ")}${geoPreview.count > 3 ? "…" : ""}` : ""}</span>
                  : <span className="sr-muted">Enter one or more markets to see nearby theaters.</span>}
              </div>
            </div>
          )}
        </div>
        <div className="sr-field"><label className="sr-label">Creative <span className="sr-muted" style={{ fontWeight: 400 }}>· poster / key art</span></label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input className="sr-input" value={form.creative_url} onChange={set("creative_url")} placeholder="https://…/poster.jpg" data-testid="dooh-creative" style={{ flex: 1 }} />
            <button type="button" className="sr-btn sr-btn-ghost sr-btn-sm" onClick={openPicker} data-testid="dooh-pick-creative" style={{ whiteSpace: "nowrap" }}><Film size={14} /> Pick from a movie</button>
          </div>
          {form.creative_url && <img src={form.creative_url} alt="selected creative" style={{ marginTop: 8, height: 68, borderRadius: 8, border: "1px solid var(--border)" }} data-testid="dooh-creative-preview" />}
        </div>
        {parseFloat(form.budget) > 0 && (
          <p className="sr-muted" style={{ fontSize: 12.5, margin: "0 0 8px" }} data-testid="dooh-est">
            <Eye size={12} style={{ verticalAlign: "-2px" }} /> Estimated ~{estViews(form.budget).toLocaleString()} impressions at a ~$10 CPM
          </p>
        )}
        <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={create} disabled={busy} data-testid="dooh-create">
          {busy ? <Loader2 size={13} className="sr-spin" /> : <><Plus size={14} /> Save DOOH order</>}
        </button>
      </div>

      {/* Creative picker */}
      {pickerOpen && (
        <div onClick={() => setPickerOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(8,16,30,0.55)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} data-testid="dooh-creative-picker">
          <div onClick={(e) => e.stopPropagation()} className="sr-card" style={{ maxWidth: 680, width: "100%", maxHeight: "80vh", overflow: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <strong style={{ color: "var(--navy)" }}>Pick a creative from a movie</strong>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setPickerOpen(false)} data-testid="dooh-picker-close"><X size={14} /></button>
            </div>
            {movies === null ? (
              <div style={{ textAlign: "center", padding: 30 }}><Loader2 className="sr-spin" /></div>
            ) : movies.length === 0 ? (
              <p className="sr-muted" style={{ fontSize: 13.5 }} data-testid="dooh-picker-empty">No movie creatives yet. Add a movie with a poster (or generate ad sizes) in <strong>Promote a Movie</strong>, then it'll appear here.</p>
            ) : (
              <div style={{ display: "grid", gap: 14 }}>
                {movies.map((m) => (
                  <div key={m.item_id} data-testid={`dooh-picker-movie-${m.item_id}`}>
                    <div style={{ color: "var(--navy)", fontWeight: 600, fontSize: 13.5, marginBottom: 6 }}>{m.name}</div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      {m.creatives.map((c) => (
                        <button key={c.key + c.url} onClick={() => chooseCreative(c.url, m.item_id)} data-testid={`dooh-pick-${m.item_id}-${c.key}`}
                          style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 0, background: "#0F172A", cursor: "pointer", overflow: "hidden", width: 92 }} title={c.label}>
                          <img src={c.url} alt={c.label} style={{ width: 92, height: 92, objectFit: "cover", display: "block" }} />
                          <div style={{ fontSize: 10.5, color: "#fff", padding: "3px 4px", textAlign: "center" }}>{c.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Orders */}
      <h2 style={{ color: "var(--navy)", fontSize: 15, margin: "20px 0 8px" }}>Your DOOH orders</h2>
      {orders.length === 0 ? (
        <div className="sr-card sr-empty" style={{ textAlign: "center", padding: 30 }} data-testid="dooh-empty">
          <Tv size={24} style={{ color: "var(--muted)" }} /><p style={{ marginTop: 8 }}>No DOOH orders yet.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {orders.map((o) => {
            const [slabel, scls] = STATUS[o.status] || STATUS.draft;
            const pacing = o.pacing;
            const [plabel, pcls] = pacing ? (PACE[pacing.status] || PACE.awaiting) : [null, null];
            const dv = delivery[o.id];
            return (
              <div key={o.id} className="sr-card" data-testid={`dooh-order-${o.id}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <strong style={{ color: "var(--navy)" }}>{o.name}</strong>
                  <span className={`sr-badge ${scls}`} data-testid={`dooh-status-${o.id}`}>{slabel}</span>
                  {pacing && <span className={`sr-badge ${pcls}`} data-testid={`dooh-pace-${o.id}`}><Gauge size={11} /> {plabel}{pacing.pct != null ? ` · ${pacing.pct}%` : ""}</span>}
                  {(o.geofence_applied || o.geofence) && <span className="sr-badge" style={{ background: "#E0ECFF", color: "#1D4ED8" }} data-testid={`dooh-geo-${o.id}`}><MapPin size={11} /> Geofenced{o.geofence_points ? ` · ${o.geofence_points} theaters` : ""}</span>}
                  <span className="sr-muted" style={{ fontSize: 12.5, marginLeft: "auto" }}>{money(o.budget)} · {o.start} → {o.end}</span>
                </div>
                <div className="sr-muted" style={{ fontSize: 12.5, marginTop: 4 }}>
                  {(o.markets || []).length > 0 ? o.markets.join(", ") : "No markets set"} · Est. ~{Number(o.estimated_views).toLocaleString()} impressions
                  {o.stackadapt_id && <> · Ad platform #{o.stackadapt_id}{o.sa_is_draft ? " (draft)" : ""}</>}
                </div>
                {pacing && <div className="sr-muted" style={{ fontSize: 12.5, marginTop: 4 }} data-testid={`dooh-pace-msg-${o.id}`}>{pacing.message}</div>}
                {o.error && <div style={{ color: "#B42318", fontSize: 12.5, marginTop: 4 }}>{o.error}</div>}
                {dv && (
                  <div style={{ marginTop: 6, fontSize: 13, color: "var(--navy)" }} data-testid={`dooh-delivery-${o.id}`}>
                    <BarChart3 size={13} style={{ verticalAlign: "-2px" }} /> {Number(dv.impressions).toLocaleString()} impressions · {Number(dv.views).toLocaleString()} views · {money(dv.cost)} spend
                    {dv.estimated && <span className="sr-muted"> (estimate)</span>}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  {o.status !== "submitted" && status?.connected && status?.can_submit && (
                    <button className="sr-btn sr-btn-sm sr-btn-primary" disabled={actId === o.id} onClick={() => act(o.id, () => sr.submitDoohOrder(o.id), "Submitted to ad platform")} data-testid={`dooh-submit-${o.id}`}>
                      {actId === o.id ? <Loader2 size={12} className="sr-spin" /> : <><Send size={12} /> Take live on the ad platform</>}
                    </button>
                  )}
                  {o.status !== "submitted" && status?.connected && !status?.can_submit && (
                    <span className="sr-muted" style={{ fontSize: 12, alignSelf: "center" }} data-testid={`dooh-await-admin-${o.id}`}>An admin takes this live on the ad platform</span>
                  )}
                  <button className="sr-btn sr-btn-sm sr-btn-ghost" disabled={actId === o.id} onClick={() => getDelivery(o.id)} data-testid={`dooh-delivery-btn-${o.id}`}><BarChart3 size={12} /> Delivery</button>
                  <button className="sr-btn sr-btn-sm sr-btn-ghost" disabled={actId === o.id} onClick={() => act(o.id, () => sr.deleteDoohOrder(o.id), "Order removed")} data-testid={`dooh-delete-${o.id}`}><Trash2 size={12} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
